import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useAuth } from '@/contexts/AuthContext';
import { LoginSchema, type LoginValues } from './LoginSchema';

interface LocationState {
  from?: { pathname?: string };
}

export default function Login() {
  const { login, error, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from?.pathname || '/dashboard';

  const [formError, setFormError] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sign in</CardTitle>
        <CardDescription>Welcome back. Sign in to the staff dashboard.</CardDescription>
      </CardHeader>
      <CardContent>
        <LoginForm
          isLoading={isLoading}
          onSubmit={async (values) => {
            setFormError(null);
            const result = await login(values.email, values.password);

            // Neither challenge should occur now that every user gets a
            // permanent password via the invite / reset link flows — but if
            // Cognito still reports one (a legacy FORCE_CHANGE_PASSWORD or
            // RESET_REQUIRED account), send them to request a branded reset
            // link rather than an inline set-password form.
            if (
              result?.challenge === 'NEW_PASSWORD_REQUIRED' ||
              result?.challenge === 'RESET_PASSWORD'
            ) {
              navigate('/auth/forgot-password', {
                state: { email: result.email ?? values.email },
              });
              return;
            }
            if (result?.success) {
              navigate(from, { replace: true });
              return;
            }
            if (result?.error) setFormError(result.error);
          }}
          error={formError || error}
        />
      </CardContent>
    </Card>
  );
}

interface LoginFormProps {
  onSubmit: (values: LoginValues) => Promise<void> | void;
  isLoading: boolean;
  error: string | null;
}

function LoginForm({ onSubmit, isLoading, error }: LoginFormProps) {
  const form = useForm<LoginValues>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '', rememberMe: false },
    mode: 'onTouched',
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  autoComplete="email"
                  autoFocus
                  disabled={isLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="password"
                  autoComplete="current-password"
                  disabled={isLoading}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="rememberMe"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-2 space-y-0">
              <FormControl>
                <Checkbox
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                  disabled={isLoading}
                />
              </FormControl>
              <Label className="!mt-0 text-sm font-normal">Remember me</Label>
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? 'Signing in…' : 'Sign in'}
        </Button>
        <div className="flex items-center justify-between text-sm">
          <Link to="/auth/forgot-password" className="text-primary hover:underline">
            Forgot password?
          </Link>
          <Link to="/" className="text-muted-foreground hover:text-foreground">
            ← Back to home
          </Link>
        </div>
      </form>
    </Form>
  );
}
